import { Button } from '@mui/material';
import { useState, useCallback, useRef, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { prepareZXingModule, readBarcodes } from 'zxing-wasm';
import { CameraVideoCanvas2 } from './CameraVideoCanvas2';

// Normalize GS1-128 result: remove GS (group separator) chars for consistent comparison
function normalizeGs1Code(code: string): string {
  // eslint-disable-next-line no-control-regex
  return code.replace(/\u001d/g, '');
}

// Extract the primary identifier from a GS1-128 barcode
function parseGs1Barcode(rawText: string): string {
  // eslint-disable-next-line no-control-regex
  return rawText.replace(/\u001d/g, String.fromCharCode(29));
}

async function decodeBarcodeWasm(imageData: ImageData): Promise<string | null> {
  const results = await readBarcodes(imageData, {
    formats: [
      'UPC-A', 'UPC-E', 'EAN-13', 'EAN-8', 'DataBar', 'DataBarExpanded', 'DataBarLimited',
      'Code128', 'ITF', 'Code39',
    ],
    tryHarder: true,
    maxNumberOfSymbols: 1,
  });
  if (results.length > 0) {
    return results[0].text;
  }
  return null;
}

interface WasmBarcodeScannerProps {
  onLog?: (msg: string) => void;
  onCapture: (code: string) => void;
  lastScannedCode?: string;
}

export const WasmBarcodeScanner2 = ({ onLog, onCapture, lastScannedCode = '' }: WasmBarcodeScannerProps) => {
  const [scannedCode, setScannedCode] = useState<string>(lastScannedCode);
  const [scanRequested, setScanRequested] = useState<boolean>(false);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanBuffer, setScanBuffer] = useState<string[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const [failureCount, setFailureCount] = useState<number>(0);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);
  const scanStartTimeRef = useRef<number | null>(null);

  // Prepare ZXing module
  prepareZXingModule({
    overrides: {
      locateFile: () => '/wasm/zxing_full.wasm',
    },
  });

  // Timeout for "no barcode detected within 60 seconds"
  useEffect(() => {
    if (scanRequested) {
      scanStartTimeRef.current = Date.now();
      const timeout = setTimeout(() => {
        setScanError('No barcode detected. Hold steady, move closer, and keep the barcode inside the frame.');
        setFailureCount(f => f + 1);
        setIsScanning(false);
        setScanRequested(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [scanRequested]);

  // Camera error handling (simulate permission denied/no stream)
  const handleCameraError = useCallback((err: unknown) => {
    setCameraError('Camera access is required to scan. Enable permissions and try again.');
    setScanRequested(false);
    setIsScanning(false);
  }, []);

  // Main frame handler
  const handleFrame = useCallback(async (imageData: ImageData) => {
    if (!scanRequested || isScanning) return;
    setIsScanning(true);

    try {
      const result = await decodeBarcodeWasm(imageData);
      if (result) {
        onLog?.(`Raw scan result: ${result}`);
        setScanBuffer(prev => {
          const updated = [...prev, result].slice(-5);
          // Use normalized comparison for GS1-128 barcodes (ignore GS separator differences)
          const normalizedCounts = updated.reduce((acc, code) => {
            const normalized = normalizeGs1Code(code);
            acc[normalized] = (acc[normalized] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          const [mostCommonNormalized, count] = Object.entries(normalizedCounts).sort((a, b) => b[1] - a[1])[0] || [];
          // Accept with 2 consistent reads (GS1-128 is harder to get 3 identical reads)
          if (count >= 2) {
            // Find the original (non-normalized) code that matches
            const finalCode = updated.find(c => normalizeGs1Code(c) === mostCommonNormalized) || result;
            setScannedCode(finalCode);
            setScanError(null);
            setFailureCount(0);
            onLog?.(`Scanned: ${finalCode}`);
            setScanRequested(false);
            setIsScanning(false);
            onCapture(finalCode);
            return [];
          }
          setIsScanning(false);
          return updated;
        });
      } else {
        // If 15s has not elapsed, treat as a normal failure
        if (scanStartTimeRef.current && Date.now() - scanStartTimeRef.current < 60000) {
          setFailureCount(f => {
            const newCount = f + 1;
            if (newCount >= 3) {
              setScanError('Still having trouble reading this barcode. Try better lighting, reduce glare, or move closer.');
            } else {
              setScanError('No barcode detected. Hold steady, move closer, and keep the barcode inside the frame.');
            }
            return newCount;
          });
        }
        setIsScanning(false);
        //setScanRequested(false); // Stop scanning after a failed frame
        setScanBuffer([]);
      }
    } catch (err: unknown) {
      handleCameraError(err);
      setIsScanning(false);
      setScanRequested(false); // Also stop scanning on error
    }
  }, [scanRequested, isScanning, onLog, onCapture, handleCameraError]);

  const handleScanButtonClick = () => {
    setScanRequested(true);
    setScanBuffer([]);
    setScanError(null);
    setFailureCount(0);
    setCameraError(null);
  };

  // Error message style at the top
  const errorStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    background: 'rgba(200,0,0,0.85)',
    color: '#fff',
    padding: '10px 8px',
    borderRadius: '8px 8px 0 0',
    textAlign: 'center',
    fontSize: 16,
    zIndex: 2,
    pointerEvents: 'none',
    minHeight: 24,
  };

  const loadingOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    minHeight: 260,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    background: 'rgba(0,0,0,0.75)',
    borderRadius: 8,
    zIndex: 3,
    color: '#fff',
    fontSize: 15,
  };

  // Layout fix: stack button and code vertically, keep compact
  return (
    <div style={{ position: 'relative', width: '100%',maxWidth: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ position: 'relative', width: '100%', flexGrow: 1, flexShrink: 1, minHeight: 0, background: '#222', borderRadius: 8, overflow: 'hidden' }}>
        {!isCameraReady && (
          <div style={loadingOverlayStyle}>

           
            <CircularProgress color="inherit"
              size={40} />
            <span>Starting camera...</span>
          </div>
        )}
        {(scanError || cameraError) && (
          <div style={errorStyle}>
            {cameraError || scanError}
          </div>
        )}
    
        <CameraVideoCanvas2
          onFrame={handleFrame}
          onLog={onLog}
          onReady={() => setIsCameraReady(true)}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, marginTop: 12, flexShrink: 0, paddingLeft: 5, paddingRight:5 }}>
        <Button
          variant="primary"
          color="primary"
          onClick={handleScanButtonClick}
          disabled={!isCameraReady || scanRequested || isScanning}
          style={{ width: '100%' }}
        >
          {(scanRequested || isScanning) ? 'Scanning...' : 'Scan Barcode'}
        </Button>
        <div style={{
          background: '#f5f5f5',
          borderRadius: 6,
          padding: '8px 12px',
          textAlign: 'center',
          fontSize: 16,
          minHeight: 32,
          marginTop: 4,
          wordBreak: 'break-all',
        }}>
          <strong>Last scanned code:</strong> {scannedCode}
        </div>
      </div>
    </div>
  );
};